import React from 'react';
import {ANNOTATE_IGNORE_PROPS} from 'client/components/PracticeView/Annotations/engine/annotationTextProjection';

interface PopoverRenderArgs {
  anchorNode?: HTMLElement | null;
}

interface Props {
  activeIndex?: number | null;
  anchorNode?: HTMLElement | null;
  rendererWrapperNode?: HTMLElement | null;
  onReset: () => void;
  children?: React.ReactNode | ((args: PopoverRenderArgs) => React.ReactNode);
}

export default class PassageCorrectionPopover extends React.Component<Props> {
  static defaultProps = {
    onReset: () => {}
  };

  declare wrapperNode: HTMLDivElement | null;

  constructor(props: Props) {
    super(props);
    this.wrapperNode = null;
  }

  componentDidMount() {
    global.addEventListener('click', this.handleClickOutsideComponent);
    global.addEventListener('keydown', this.handleEscapeKeyPress);
    this.positionPopoverHandler();
  }

  componentDidUpdate(prevProps: Props) {
    this.positionPopoverHandler(prevProps.anchorNode);
  }

  componentWillUnmount() {
    // Capture must match the addEventListener call below, or removal is a no-op and the
    // listener keeps firing after unmount.
    global.removeEventListener('scroll', this.positionPopover, true);
    global.removeEventListener('click', this.handleClickOutsideComponent);
    global.removeEventListener('keydown', this.handleEscapeKeyPress);
  }

  handleClickOutsideComponent = (e: MouseEvent) => {
    if (
      this.props.rendererWrapperNode &&
      this.isActive() &&
      !this.props.rendererWrapperNode.contains(e.target as Node)
    ) {
      this.props.onReset();
    }
  };

  handleEscapeKeyPress = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && this.isActive()) {
      this.props.onReset();
    }
  };

  positionPopoverHandler(previousAnchorNode: HTMLElement | null = null) {
    if (!this.isActive()) {
      global.removeEventListener('scroll', this.positionPopover, true);
      return;
    }

    if (previousAnchorNode !== this.props.anchorNode) {
      global.removeEventListener('scroll', this.positionPopover, true);
      this.positionPopover();
      global.addEventListener('scroll', this.positionPopover, true);
    }
  }

  positionPopover = () => {
    if (!this.wrapperNode) {
      return;
    }
    const anchorRect = this.props.anchorNode!.getBoundingClientRect();
    const popoverRect = this.wrapperNode!.getBoundingClientRect();

    const popoverWidth = popoverRect.width || 250;
    const margin = 8;
    const viewportWidth = window.innerWidth;

    this.wrapperNode!.style.position = 'fixed';
    this.wrapperNode!.style.top = `${anchorRect.bottom}px`;
    this.wrapperNode!.style.left = 'auto';
    this.wrapperNode!.style.right = 'auto';

    const rightAlignedLeft = anchorRect.right - popoverWidth;
    if (rightAlignedLeft >= margin) {
      // Right-aligned fits
      this.wrapperNode!.style.right = `${viewportWidth - anchorRect.right}px`;
    } else if (anchorRect.left + popoverWidth <= viewportWidth - margin) {
      // Left-aligned fits
      this.wrapperNode!.style.left = `${Math.max(margin, anchorRect.left)}px`;
    } else {
      // fallback if neither fits
      const constrainedLeft = Math.max(
        margin,
        Math.min(anchorRect.left, viewportWidth - popoverWidth - margin)
      );
      this.wrapperNode!.style.left = `${constrainedLeft}px`;
    }
  };

  isActive() {
    return Boolean(this.props.activeIndex !== null && this.props.anchorNode);
  }

  setWrapperNode = (node: HTMLDivElement | null) => {
    this.wrapperNode = node;
  };

  render() {
    if (!this.isActive()) {
      return null;
    }
    const {children, anchorNode} = this.props;
    return (
      <div
        {...ANNOTATE_IGNORE_PROPS}
        className='passage-correction-popover card card--no-padding no-arrow'
        ref={this.setWrapperNode}
      >
        {typeof children === 'function'
          ? children({
              anchorNode
            })
          : children}
      </div>
    );
  }
}
